import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { supabase } from '../lib/supabase';

function EditPersonalInstallmentForm({ installmentGroup, onInstallmentUpdated, onCancel }) {
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    amountRaw: '',
    category: '',
    installment_count: 0,
    payment_source: '',
    first_installment_date: ''
  });

  const [categories, setCategories] = useState([]);
  const [paymentSources, setPaymentSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (installmentGroup && installmentGroup.length > 0) {
      const first = installmentGroup[0];
      const total = installmentGroup.reduce((sum, p) => sum + Math.abs(p.value), 0);
      
      setFormData({
        description: first.description,
        amount: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(total),
        amountRaw: total,
        category: first.category_id,
        installment_count: first.installment_count,
        payment_source: first.payment_source_id || '',
        first_installment_date: first.date
      });

      // Buscar a origem de pagamento selecionada
      if (first.payment_source_id) {
        const fetchSelectedSource = async () => {
          const { data } = await supabase
            .from('payment_sources')
            .select('*')
            .eq('id', first.payment_source_id)
            .single();
          setSelectedSource(data);
        };
        fetchSelectedSource();
      }
    }

    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('personal_categories')
          .select('*')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (error) {
        console.error('Erro ao buscar categorias:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao carregar categorias',
          severity: 'error'
        });
      }
    };

    const fetchPaymentSources = async () => {
      try {
        const { data, error } = await supabase
          .from('payment_sources')
          .select('*')
          .eq('is_active', true)
          .order('name');
        if (error) throw error;
        setPaymentSources(data || []);
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Erro ao carregar origens de pagamento',
          severity: 'error'
        });
      }
    };

    fetchCategories();
    fetchPaymentSources();
  }, [installmentGroup]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'payment_source') {
      const found = paymentSources.find(src => src.id === value);
      setSelectedSource(found || null);
    }
  };

  const handleValueChange = (e) => {
    const value = e.target.value;
    // Remove caracteres não numéricos
    const numericValue = value.replace(/\D/g, '');
    // Formata como moeda brasileira
    const formattedValue = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue / 100);

    setFormData(prev => ({
      ...prev,
      amount: formattedValue,
      amountRaw: numericValue / 100
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!installmentGroup || installmentGroup.length === 0) return;

    // Validação simples
    if (!formData.description.trim() || !formData.amount || !formData.category || !formData.installment_count) {
      setSnackbar({
        open: true,
        message: 'Preencha todos os campos obrigatórios',
        severity: 'error'
      });
      return;
    }

    // Converte o valor formatado para número
    const value = parseFloat(formData.amount.replace(/[R$\s.]/g, '').replace(',', '.'));
    if (isNaN(value) || value <= 0) {
      setSnackbar({
        open: true,
        message: 'Valor inválido',
        severity: 'error'
      });
      return;
    }

    const count = parseInt(formData.installment_count, 10);
    if (isNaN(count) || count < 2) {
      setSnackbar({
        open: true,
        message: 'Número de parcelas deve ser 2 ou mais',
        severity: 'error'
      });
      return;
    }

    // Validar data da primeira parcela
    if (!formData.first_installment_date) {
      setSnackbar({
        open: true,
        message: 'Data da primeira parcela é obrigatória',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const groupId = installmentGroup[0].installment_group_id;

      // Primeiro, deletar as parcelas existentes (soft delete)
      const { error: deleteError } = await supabase
        .from('personal_transactions')
        .update({ deleted_at: new Date().toISOString() })
        .eq('installment_group_id', groupId);

      if (deleteError) throw deleteError;

      // Criar novas parcelas com os dados atualizados
      const transactions = [];
      const valuePerInstallment = Math.floor((value / count) * 100) / 100;
      let soma = 0;

      // Usar a data da primeira parcela informada pelo usuário
      const firstInstallmentDate = new Date(formData.first_installment_date);
      
      for (let i = 0; i < count; i++) {
        let installmentDate;

        if (selectedSource && selectedSource.is_credit_card) {
          // Para cartão de crédito: sempre usar o dia 7 do mês
          // A primeira parcela usa a data informada, as demais usam dia 7
          if (i === 0) {
            installmentDate = new Date(firstInstallmentDate);
          } else {
            const targetDate = new Date(firstInstallmentDate);
            targetDate.setMonth(targetDate.getMonth() + i);
            installmentDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), 7);
          }
        } else {
          // Para outras formas de pagamento: manter o mesmo dia do mês
          installmentDate = new Date(firstInstallmentDate);
          installmentDate.setMonth(installmentDate.getMonth() + i);
        }

        let parcelaValue = valuePerInstallment;
        if (i === count - 1) {
          parcelaValue = parseFloat((value - soma).toFixed(2));
        }
        soma += parcelaValue;
        transactions.push({
          type: 'saida',
          description: formData.description.trim(),
          value: -parcelaValue,
          category_id: formData.category,
          date: installmentDate.toISOString().split('T')[0],
          installment_count: count,
          installment_number: i + 1,
          installment_group_id: groupId,
          payment_source_id: formData.payment_source || null
        });
      }

      const { error: insertError } = await supabase
        .from('personal_transactions')
        .insert(transactions);

      if (insertError) throw insertError;

      setSnackbar({
        open: true,
        message: 'Parcelamento atualizado com sucesso',
        severity: 'success'
      });

      if (onInstallmentUpdated) onInstallmentUpdated();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar parcelamento: ' + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (!installmentGroup || installmentGroup.length === 0) {
    return <Typography>Carregando...</Typography>;
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <TextField
            label="Descrição"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Origem do Pagamento"
            name="payment_source"
            value={formData.payment_source}
            onChange={handleChange}
            required
            fullWidth
          >
            <MenuItem value="">Selecione a origem</MenuItem>
            {paymentSources.map((source) => (
              <MenuItem key={source.id} value={source.id}>
                {source.name} {source.is_credit_card ? '(Cartão)' : ''}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Categoria"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            fullWidth
          >
            <MenuItem value="">Selecione uma categoria</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.parent_id ? `→ ${category.name}` : category.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Valor Total"
            name="amount"
            value={formData.amount}
            onChange={handleValueChange}
            required
            fullWidth
            InputProps={{
              inputProps: {
                inputMode: 'numeric'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Número de Parcelas"
            name="installment_count"
            type="number"
            value={formData.installment_count}
            onChange={handleChange}
            required
            fullWidth
            inputProps={{ min: 2 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Data da Primeira Parcela"
            type="date"
            name="first_installment_date"
            value={formData.first_installment_date}
            onChange={handleChange}
            required
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {selectedSource && selectedSource.is_credit_card 
              ? "Para cartão de crédito: a primeira parcela usará a data informada, as demais serão ajustadas para o dia 7 do mês"
              : "As demais parcelas serão calculadas automaticamente mantendo o mesmo dia do mês"
            }
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Atualizar Parcelamento'}
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EditPersonalInstallmentForm;
